import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { History, Visibility } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { CustomChip } from 'src/shared/components/organisms/customChip';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import DataGridWrapper from 'src/shared/components/organisms/dataGridWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { HelpPageVersion } from '../models/helpPageVersion';
import { getHelpPageVersions, restoreHelpPageVersion } from '../services/helpPageService';
import { HelpPageVersionPreviewDialog } from './helpPageVersionPreviewDialog';

type HelpPageVersionPanelProps = {
  helpPageId: string;
  /** Arkiverad artikel: versionerna går att granska men inte återställa. */
  readOnly?: boolean;
  /** Anropas efter en lyckad återställning, så listan/sidan runt omkring kan uppdatera sig. */
  onRestored?: () => void;
};

export const HelpPageVersionPanel = (props: HelpPageVersionPanelProps) => {
  const { helpPageId, readOnly = false, onRestored } = props;
  const { t } = useTranslation(['helpArticles', 'common']);
  const isMounted = useIsMounted();

  const [versions, setVersions] = useState<HelpPageVersion[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [previewVersion, setPreviewVersion] = useState<HelpPageVersion | null>(null);
  const [versionToRestore, setVersionToRestore] = useState<HelpPageVersion | null>(null);
  // Egen flagga för dialogen, så att versionen finns kvar i titeln medan dialogen stängs.
  const [restoreDialogOpen, setRestoreDialogOpen] = useState<boolean>(false);

  const fetchVersions = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getHelpPageVersions(helpPageId);
      if (isMounted()) {
        setVersions(result.data);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [helpPageId, isMounted]);

  useEffect(() => {
    if (isMounted()) fetchVersions();
  }, [fetchVersions, isMounted]);

  const handleRestore = async (version: HelpPageVersion) => {
    setLoading(true);
    try {
      await restoreHelpPageVersion(helpPageId, version.id);
      if (isMounted()) {
        toast.success(t('helpArticles:versions.restoredSuccess') as string);
        fetchVersions();
        if (onRestored) onRestored();
      }
    } catch (e) {
      ConsoleHelper.log(e);
      if (isMounted()) setLoading(false);
    }
  };

  return (
    <>
      <DataGridWrapper loading={loading}>
        {versions.length === 0 && !loading ? (
          <Typography variant='body2' color='text.secondary' sx={{ p: 2 }}>
            {t('helpArticles:versions.empty')}
          </Typography>
        ) : (
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>{t('helpArticles:versions.version')}</TableCell>
                <TableCell>{t('helpArticles:versions.createdBy')}</TableCell>
                <TableCell>{t('helpArticles:versions.createdAt')}</TableCell>
                <TableCell>{t('helpArticles:versions.changeSummary')}</TableCell>
                <TableCell align='right' />
              </TableRow>
            </TableHead>
            <TableBody>
              {versions.map((version) => (
                <TableRow key={version.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {version.versionNumber}
                      {version.isPublished && <CustomChip label={t('helpArticles:versions.current')} color='success' size='small' smallRadius />}
                    </Box>
                  </TableCell>
                  <TableCell>{version.createdBy}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{DateHelper.getDateTime(version.createdAt)}</TableCell>
                  <TableCell>{version.changeSummary}</TableCell>
                  <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                    <Button size='small' startIcon={<Visibility fontSize='small' />} onClick={() => setPreviewVersion(version)}>
                      {t('helpArticles:versions.preview')}
                    </Button>
                    <Button
                      size='small'
                      startIcon={<History fontSize='small' />}
                      disabled={readOnly || version.isPublished}
                      onClick={() => {
                        setVersionToRestore(version);
                        setRestoreDialogOpen(true);
                      }}
                    >
                      {t('helpArticles:versions.restore')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DataGridWrapper>

      <HelpPageVersionPreviewDialog version={previewVersion} onClose={() => setPreviewVersion(null)} />

      <DeleteConfirmationDialog
        open={restoreDialogOpen}
        title={t('helpArticles:versions.restoreConfirmTitle', { versionNumber: versionToRestore?.versionNumber })}
        modaltext={t('helpArticles:versions.restoreConfirmText', { versionNumber: versionToRestore?.versionNumber })}
        value={versionToRestore?.id ?? ''}
        confirmButtonText={t('helpArticles:versions.restore')}
        onClose={(value) => {
          // Dialogen stängs direkt, så att OK inte kan ge en andra återställning medan anropet pågår.
          setRestoreDialogOpen(false);
          if (value && versionToRestore) handleRestore(versionToRestore);
        }}
      />
    </>
  );
};
